import React, { useState, useEffect, useCallback } from 'react';
import { Smartphone, Database, Shield, TrendingUp, Users, Zap, Code, CreditCard, BarChart3, Lock, MapPin, FileText, Server, GitBranch, TestTube, DollarSign, Home, Building, LineChart, Activity, Download, Search, Settings } from 'lucide-react';

const slides = [
  // Slide 1: Technology Architecture Overview
  {
    id: 1,
    content: (
      <div className="slide">
        <div className="slide-number">1/8</div>
        <h1>Technology Architecture Overview</h1>
        <p className="subtitle">Modern, Scalable Real Estate Investment Platform</p>
        
        <div className="three-column-layout">
          <div className="tech-card tech-card-blue">
            <div className="tech-header">
              <Smartphone className="tech-icon" />
              <h3>Mobile-First Platform</h3>
            </div>
            <ul>
              <li>React Native + Expo SDK 53</li>
              <li>Cross-platform iOS/Android</li>
              <li>TypeScript for type safety</li>
              <li>60fps performance optimization</li>
            </ul>
          </div>
          
          <div className="tech-card tech-card-green">
            <div className="tech-header">
              <Database className="tech-icon" />
              <h3>Real-Time Data Infrastructure</h3>
            </div>
            <ul>
              <li>Supabase PostgreSQL database</li>
              <li>Real-time subscriptions</li>
              <li>Zillow API for property data</li>
              <li>MLS data integration</li>
            </ul>
          </div>

          <div className="tech-card tech-card-purple">
            <div className="tech-header">
              <Shield className="tech-icon" />
              <h3>Enterprise Security</h3>
            </div>
            <ul>
              <li>End-to-end encryption</li>
              <li>Biometric authentication</li>
              <li>SOC 2 Type II compliance</li>
              <li>SEC-compliant framework</li>
            </ul>
          </div>

          <div className="tech-card tech-card-orange">
            <div className="tech-header">
              <TrendingUp className="tech-icon" />
              <h3>Financial Technology</h3>
            </div>
            <ul>
              <li>Stripe payment processing</li>
              <li>Plaid banking integration</li>
              <li>Automated rent collection</li>
              <li>Real-time portfolio tracking</li>
            </ul>
          </div>

          <div className="tech-card tech-card-teal">
            <div className="tech-header">
              <Users className="tech-icon" />
              <h3>Community Features</h3>
            </div>
            <ul>
              <li>Real-time voting system</li>
              <li>Property shareholder forums</li>
              <li>Financial transparency tools</li>
              <li>Event management system</li>
            </ul>
          </div>

          <div className="tech-card tech-card-yellow">
            <div className="tech-header">
              <Zap className="tech-icon" />
              <h3>Performance & Scale</h3>
            </div>
            <ul>
              <li>CDN-optimized delivery</li>
              <li>Horizontal auto-scaling</li>
              <li>99.9% uptime SLA</li>
              <li>Sub-100ms API response</li>
            </ul>
          </div>
        </div>
      </div>
    )
  },

  // Slide 2: Platform Capabilities (Split into two slides)
  {
    id: 2,
    content: (
      <div className="slide">
        <div className="slide-number">2/8</div>
        <h1>Platform Capabilities</h1>
        <p className="subtitle">Features for Investors & Landlords</p>
        
        <div className="two-column-layout">
          <div className="user-type-section">
            <div className="user-type-header user-type-blue">
              <TrendingUp className="user-type-icon" />
              <h3>App Investors</h3>
              <p>External users investing in property shares</p>
            </div>
            
            <div className="feature-group">
              <h4><Search className="feature-icon" />Property Discovery</h4>
              <ul>
                <li>Browse available properties</li>
                <li>Advanced search and filtering</li>
                <li>Neighborhood analytics</li>
                <li>Property comparison tools</li>
              </ul>
            </div>

            <div className="feature-group">
              <h4><DollarSign className="feature-icon" />Trading & Portfolio</h4>
              <ul>
                <li>Buy/sell property shares</li>
                <li>Portfolio management</li>
                <li>Real-time performance tracking</li>
                <li>Monthly rental income</li>
              </ul>
            </div>
          </div>

          <div className="user-type-section">
            <div className="user-type-header user-type-green">
              <Building className="user-type-icon" />
              <h3>Landlords</h3>
              <p>Property owners monetizing equity</p>
            </div>
            
            <div className="feature-group">
              <h4><Home className="feature-icon" />Property Management</h4>
              <ul>
                <li>Property listing and setup</li>
                <li>Share allocation management</li>
                <li>Tenant relationship oversight</li>
                <li>Maintenance coordination</li>
              </ul>
            </div>

            <div className="feature-group">
              <h4><DollarSign className="feature-icon" />Equity Liquidity</h4>
              <ul>
                <li>List property shares for sale</li>
                <li>Market-based pricing</li>
                <li>Flexible sale percentages</li>
                <li>Liquidity timing control</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )
  },

  // Slide 3: Platform Capabilities Part 2
  {
    id: 3,
    content: (
      <div className="slide">
        <div className="slide-number">3/8</div>
        <h1>Platform Capabilities</h1>
        <p className="subtitle">Features for Renters & Analytics</p>
        
        <div className="two-column-layout">
          <div className="user-type-section">
            <div className="user-type-header user-type-purple">
              <Home className="user-type-icon" />
              <h3>GusHome Renters</h3>
              <p>Tenants building equity while renting</p>
            </div>
            
            <div className="feature-group">
              <h4><CreditCard className="feature-icon" />Rent & Payment</h4>
              <ul>
                <li>Monthly rent payment processing</li>
                <li>Rent pool management</li>
                <li>Auto-pay setup and control</li>
                <li>Payment history and receipts</li>
              </ul>
            </div>

            <div className="feature-group">
              <h4><TrendingUp className="feature-icon" />Equity Building</h4>
              <ul>
                <li>Automatic 5% rent investment</li>
                <li>Building share accumulation</li>
                <li>Monthly rental income</li>
                <li>Portfolio value tracking</li>
              </ul>
            </div>
          </div>

          <div className="analytics-section">
            <div className="user-type-header user-type-teal">
              <BarChart3 className="user-type-icon" />
              <h3>Analytics & Insights</h3>
              <p>Advanced tools for all users</p>
            </div>
            
            <div className="feature-group">
              <h4><LineChart className="feature-icon" />Investment Analytics</h4>
              <ul>
                <li>Property valuation models</li>
                <li>Market performance metrics</li>
                <li>Investment projections</li>
                <li>Tax reporting tools</li>
              </ul>
            </div>

            <div className="feature-group">
              <h4><FileText className="feature-icon" />Financial Reporting</h4>
              <ul>
                <li>Monthly performance reports</li>
                <li>NOI and cash flow tracking</li>
                <li>Expense management</li>
                <li>Tax documentation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )
  },

  // Slide 4: Data Sources & Integration
  {
    id: 4,
    content: (
      <div className="slide">
        <div className="slide-number">4/8</div>
        <h1>Data Sources & Integration</h1>
        <p className="subtitle">Comprehensive Real Estate Market Intelligence</p>
        
        <div className="three-column-layout">
          <div className="data-source-card">
            <div className="data-source-header">
              <Home className="data-source-icon" />
              <h3>Zillow API</h3>
            </div>
            <ul>
              <li>Property valuations (Zestimate)</li>
              <li>Historical price data</li>
              <li>Neighborhood analytics</li>
              <li>Comparable sales</li>
            </ul>
            <div className="data-frequency">Real-time updates</div>
          </div>

          <div className="data-source-card">
            <div className="data-source-header">
              <Building className="data-source-icon" />
              <h3>MLS Data</h3>
            </div>
            <ul>
              <li>Property listings</li>
              <li>Sales history</li>
              <li>Days on market</li>
              <li>Property specifications</li>
            </ul>
            <div className="data-frequency">Daily sync</div>
          </div>

          <div className="data-source-card">
            <div className="data-source-header">
              <BarChart3 className="data-source-icon" />
              <h3>Financial Data</h3>
            </div>
            <ul>
              <li>Property cash flows</li>
              <li>Operating expenses</li>
              <li>NOI calculations</li>
              <li>Cap rate analysis</li>
            </ul>
            <div className="data-frequency">Monthly reports</div>
          </div>

          <div className="data-source-card">
            <div className="data-source-header">
              <MapPin className="data-source-icon" />
              <h3>Location Intelligence</h3>
            </div>
            <ul>
              <li>Demographics data</li>
              <li>School ratings</li>
              <li>Crime statistics</li>
              <li>Transportation access</li>
            </ul>
            <div className="data-frequency">Weekly updates</div>
          </div>

          <div className="data-source-card">
            <div className="data-source-header">
              <Users className="data-source-icon" />
              <h3>User Data</h3>
            </div>
            <ul>
              <li>Portfolio holdings</li>
              <li>Transaction history</li>
              <li>Investment preferences</li>
              <li>Risk tolerance</li>
            </ul>
            <div className="data-frequency">Real-time</div>
          </div>

          <div className="data-processing-card">
            <h3>Data Processing Pipeline</h3>
            <div className="processing-items">
              <div className="processing-item">
                <Download className="processing-icon" />
                <span>ETL processes</span>
              </div>
              <div className="processing-item">
                <Database className="processing-icon" />
                <span>Data normalization</span>
              </div>
              <div className="processing-item">
                <Activity className="processing-icon" />
                <span>Real-time streaming</span>
              </div>
              <div className="processing-item">
                <BarChart3 className="processing-icon" />
                <span>Analytics engine</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },

  // Slide 5: Mobile Application Architecture
  {
    id: 5,
    content: (
      <div className="slide">
        <div className="slide-number">5/8</div>
        <h1>Mobile Application Architecture</h1>
        <p className="subtitle">React Native + Expo SDK 53 Platform</p>
        
        <div className="two-column-layout">
          <div className="arch-section arch-section-blue">
            <div className="arch-header">
              <Code className="arch-icon" />
              <h3>Frontend Framework</h3>
            </div>
            <div className="arch-subsections">
              <div className="arch-subsection">
                <h4>Core Technology</h4>
                <ul>
                  <li>React Native 0.76.3</li>
                  <li>Expo SDK 53+</li>
                  <li>TypeScript 5.6.3</li>
                  <li>React 18.3.1</li>
                </ul>
              </div>
              <div className="arch-subsection">
                <h4>State Management</h4>
                <ul>
                  <li>Zustand stores</li>
                  <li>React Query</li>
                  <li>React Hook Form</li>
                  <li>Zod validation</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="arch-section arch-section-green">
            <div className="arch-header">
              <Server className="arch-icon" />
              <h3>Backend Integration</h3>
            </div>
            <div className="arch-subsections">
              <div className="arch-subsection">
                <h4>Supabase Services</h4>
                <ul>
                  <li>PostgreSQL database</li>
                  <li>Real-time subscriptions</li>
                  <li>Authentication service</li>
                  <li>File storage</li>
                </ul>
              </div>
              <div className="arch-subsection">
                <h4>API Integration</h4>
                <ul>
                  <li>RESTful endpoints</li>
                  <li>GraphQL subscriptions</li>
                  <li>Webhook handling</li>
                  <li>Rate limiting</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="arch-section arch-section-white">
            <div className="arch-header">
              <Smartphone className="arch-icon" />
              <h3>Navigation & UI</h3>
            </div>
            <div className="arch-subsections">
              <div className="arch-subsection">
                <h4>Navigation</h4>
                <ul>
                  <li>React Navigation v7</li>
                  <li>Stack Navigator</li>
                  <li>Tab Navigator</li>
                  <li>Modal Navigator</li>
                </ul>
              </div>
              <div className="arch-subsection">
                <h4>UI Components</h4>
                <ul>
                  <li>NativeBase v3.4</li>
                  <li>React Native Paper</li>
                  <li>Victory Charts</li>
                  <li>Lottie Animations</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="arch-section arch-section-purple">
            <div className="arch-header">
              <CreditCard className="arch-icon" />
              <h3>Payment Integration</h3>
            </div>
            <div className="payment-items">
              <div className="payment-item">
                <h4>Stripe SDK</h4>
                <p>Payment processing, ACH transfers, card management</p>
              </div>
              <div className="payment-item">
                <h4>Plaid Link</h4>
                <p>Bank account verification and connectivity</p>
              </div>
              <div className="payment-item">
                <h4>Auto-Pay System</h4>
                <p>Recurring rent payments and investment automation</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },

  // Slide 6: Security & Compliance (Split into two slides)
  {
    id: 6,
    content: (
      <div className="slide">
        <div className="slide-number">6/8</div>
        <h1>Security & Compliance</h1>
        <p className="subtitle">Enterprise-Grade Security Framework</p>
        
        <div className="two-column-layout">
          <div className="security-section security-section-red">
            <div className="security-header">
              <Shield className="security-icon" />
              <h3>Security Framework</h3>
            </div>
            <div className="security-subsections">
              <div className="security-subsection">
                <h4>Encryption Standards</h4>
                <ul>
                  <li>AES-256 data encryption</li>
                  <li>TLS 1.3 in transit</li>
                  <li>End-to-end encryption</li>
                  <li>Hardware security modules</li>
                </ul>
              </div>
              <div className="security-subsection">
                <h4>Authentication</h4>
                <ul>
                  <li>Multi-factor authentication</li>
                  <li>Biometric verification</li>
                  <li>Device fingerprinting</li>
                  <li>Session management</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="compliance-grid">
            <div className="compliance-card compliance-card-blue">
              <h4>SOC 2 Type II</h4>
              <p>Security, availability, processing integrity</p>
            </div>
            <div className="compliance-card compliance-card-green">
              <h4>SEC Compliance</h4>
              <p>Securities regulations, investor protection</p>
            </div>
            <div className="compliance-card compliance-card-purple">
              <h4>GDPR & CCPA</h4>
              <p>Data privacy and user rights</p>
            </div>
            <div className="compliance-card compliance-card-orange">
              <h4>PCI DSS</h4>
              <p>Payment card data security</p>
            </div>
          </div>
        </div>
      </div>
    )
  },

  // Slide 7: Security & Compliance Part 2
  {
    id: 7,
    content: (
      <div className="slide">
        <div className="slide-number">7/8</div>
        <h1>Security & Compliance</h1>
        <p className="subtitle">Data Protection & Monitoring</p>
        
        <div className="two-column-layout">
          <div className="data-protection-section">
            <div className="data-protection-header">
              <Lock className="data-protection-icon" />
              <h3>Data Protection</h3>
            </div>
            <div className="protection-items">
              <div className="protection-item">
                <h4>PII Encryption</h4>
                <p>Field-level encryption, tokenization</p>
              </div>
              <div className="protection-item">
                <h4>Financial Data</h4>
                <p>Separate encrypted storage, audit trails</p>
              </div>
              <div className="protection-item">
                <h4>Access Controls</h4>
                <p>Role-based permissions, least privilege</p>
              </div>
              <div className="protection-item">
                <h4>Data Retention</h4>
                <p>Automated deletion, compliance schedules</p>
              </div>
            </div>
          </div>

          <div className="monitoring-section">
            <div className="monitoring-header">
              <Activity className="monitoring-icon" />
              <h3>Security Monitoring</h3>
            </div>
            <div className="monitoring-subsections">
              <div className="monitoring-subsection">
                <h4>Threat Detection</h4>
                <ul>
                  <li>Real-time monitoring</li>
                  <li>Anomaly detection</li>
                  <li>Fraud prevention</li>
                  <li>Incident response</li>
                </ul>
              </div>
              <div className="monitoring-subsection">
                <h4>Audit & Logging</h4>
                <ul>
                  <li>Comprehensive audit trails</li>
                  <li>Immutable logs</li>
                  <li>Real-time alerts</li>
                  <li>Compliance reporting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },

  // Slide 8: Development & Quality Assurance
  {
    id: 8,
    content: (
      <div className="slide">
        <div className="slide-number">8/8</div>
        <h1>Development & Quality Assurance</h1>
        <p className="subtitle">Best Practices for Reliable Software Delivery</p>
        
        <div className="three-column-layout">
          <div className="dev-workflow-section">
            <div className="dev-header">
              <GitBranch className="dev-icon" />
              <h3>Development Workflow</h3>
            </div>
            <div className="dev-subsections">
              <div className="dev-subsection">
                <h4>Version Control</h4>
                <ul>
                  <li>Git with GitFlow branching</li>
                  <li>Pull request reviews</li>
                  <li>Automated code scanning</li>
                  <li>Branch protection rules</li>
                </ul>
              </div>
              <div className="dev-subsection">
                <h4>Code Quality</h4>
                <ul>
                  <li>TypeScript strict mode</li>
                  <li>ESLint + Prettier</li>
                  <li>SonarQube analysis</li>
                  <li>Code coverage &gt;80%</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="testing-strategies">
            <div className="testing-header">
              <TestTube className="testing-icon" />
              <h3>Testing Strategy</h3>
            </div>
            <div className="testing-items">
              <div className="testing-item">
                <h4>Unit Testing</h4>
                <p>Jest, React Testing Library, 90%+ coverage</p>
              </div>
              <div className="testing-item">
                <h4>Integration Testing</h4>
                <p>API testing, database integration</p>
              </div>
              <div className="testing-item">
                <h4>E2E Testing</h4>
                <p>Detox for React Native, critical user flows</p>
              </div>
              <div className="testing-item">
                <h4>Performance Testing</h4>
                <p>Load testing, memory profiling</p>
              </div>
            </div>
          </div>

          <div className="devops-section">
            <div className="devops-header">
              <Settings className="devops-icon" />
              <h3>DevOps & Infrastructure</h3>
            </div>
            <div className="devops-items">
              <div className="devops-item">
                <h4>CI/CD Pipeline</h4>
                <p>GitHub Actions, automated testing, deployment</p>
              </div>
              <div className="devops-item">
                <h4>Infrastructure as Code</h4>
                <p>Terraform, reproducible environments</p>
              </div>
              <div className="devops-item">
                <h4>Monitoring</h4>
                <p>Application metrics, error tracking</p>
              </div>
              <div className="devops-item">
                <h4>Security Scanning</h4>
                <p>Dependency scanning, SAST, DAST</p>
              </div>
            </div>
          </div>

          <div className="costs-section">
            <div className="costs-header">
              <DollarSign className="costs-icon" />
              <h3>Development Costs</h3>
            </div>
            <div className="costs-items">
              <div className="costs-item">
                <span>Development Team (6 engineers)</span>
                <span>$120K/month</span>
              </div>
              <div className="costs-item">
                <span>Infrastructure & Tools</span>
                <span>$15K/month</span>
              </div>
              <div className="costs-item">
                <span>Third-party Services</span>
                <span>$25K/month</span>
              </div>
              <div className="costs-total">
                <span>Total Monthly</span>
                <span>$160K</span>
              </div>
            </div>
          </div>

          <div className="quality-metrics">
            <h3>Quality Metrics</h3>
            <div className="metrics-grid">
              <div className="metric-item">
                <div className="metric-value">95%</div>
                <div className="metric-label">Code Coverage</div>
              </div>
              <div className="metric-item">
                <div className="metric-value">&lt;1%</div>
                <div className="metric-label">Bug Rate</div>
              </div>
              <div className="metric-item">
                <div className="metric-value">99.9%</div>
                <div className="metric-label">Uptime</div>
              </div>
              <div className="metric-item">
                <div className="metric-value">2hrs</div>
                <div className="metric-label">Deploy Time</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

const TechDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Navigate to previous slide
  const goToPrevious = useCallback(() => {
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  // Navigate to next slide
  const goToNext = useCallback(() => {
    setCurrentSlide((prev) => (prev < slides.length - 1 ? prev + 1 : prev));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  return (
    <div className="pitch-deck-container">
      {/* Main slide display */}
      <div className="slides-container">
        {slides[currentSlide].content}
      </div>

      {/* Left navigation arrow */}
      <button
        className={`nav-arrow nav-arrow-left ${currentSlide === 0 ? 'disabled' : ''}`}
        onClick={goToPrevious}
        disabled={currentSlide === 0}
      >
        ←
      </button>

      {/* Right navigation arrow */}
      <button
        className={`nav-arrow nav-arrow-right ${currentSlide === slides.length - 1 ? 'disabled' : ''}`}
        onClick={goToNext}
        disabled={currentSlide === slides.length - 1}
      >
        →
      </button>

      {/* Progress indicators */}
      <div className="progress-indicators">
        {slides.map((_, index) => (
          <div
            key={index}
            className={`progress-indicator ${currentSlide === index ? 'active' : ''}`}
            onClick={() => setCurrentSlide(index)}
          />
        ))}
      </div>

      <style jsx>{`
        /* Full-screen pitch deck styles */
        .pitch-deck-container {
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          position: fixed;
          top: 0;
          left: 0;
          background: linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #0f172a 100%);
          color: #fff;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          user-select: none;
          padding-top: 60px;
          box-sizing: border-box;
        }

        .slides-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .slide {
          width: 95%;
          max-width: 1400px;
          height: 95%;
          padding: 20px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          text-align: center;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          position: relative;
          overflow-y: auto;
          box-sizing: border-box;
        }

        .slide-number {
          position: absolute;
          top: 20px;
          right: 20px;
          font-size: 0.9rem;
          opacity: 0.7;
          background: rgba(255, 255, 255, 0.2);
          padding: 5px 10px;
          border-radius: 15px;
        }

        /* Navigation arrows */
        .nav-arrow {
          position: fixed;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 1.5rem;
          z-index: 1000;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .nav-arrow:hover:not(.disabled) {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-50%) scale(1.1);
        }

        .nav-arrow.disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .nav-arrow-left {
          left: 20px;
        }

        .nav-arrow-right {
          right: 20px;
        }

        /* Progress indicators */
        .progress-indicators {
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
          z-index: 1000;
        }

        .progress-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .progress-indicator.active {
          background: rgba(255, 255, 255, 0.8);
          transform: scale(1.2);
        }

        /* Typography */
        .slide h1 {
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 800;
          margin-bottom: 15px;
          background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .slide h2 {
          font-size: clamp(1.5rem, 4vw, 2.2rem);
          font-weight: 700;
          margin-bottom: 20px;
          color: #fff;
        }

        .slide h3 {
          font-size: clamp(1rem, 3vw, 1.4rem);
          font-weight: 600;
          margin-bottom: 15px;
          color: #f8f9ff;
        }

        .slide h4 {
          font-size: clamp(0.9rem, 2.5vw, 1.2rem);
          font-weight: 600;
          margin-bottom: 10px;
          color: #f8f9ff;
        }

        .slide p, .slide li {
          font-size: clamp(0.8rem, 2vw, 1rem);
          line-height: 1.4;
          margin-bottom: 10px;
          color: #f0f1ff;
        }

        .subtitle {
          font-size: clamp(1rem, 3vw, 1.3rem);
          font-weight: 400;
          margin-bottom: 25px;
          opacity: 0.9;
        }

        /* Tech card styles */
        .tech-card {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 15px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .tech-card-blue { border-left: 4px solid #3b82f6; }
        .tech-card-green { border-left: 4px solid #10b981; }
        .tech-card-purple { border-left: 4px solid #8b5cf6; }
        .tech-card-orange { border-left: 4px solid #f59e0b; }
        .tech-card-teal { border-left: 4px solid #14b8a6; }
        .tech-card-yellow { border-left: 4px solid #eab308; }

        .tech-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .tech-icon {
          width: 20px;
          height: 20px;
          margin-right: 8px;
        }

        .tech-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }

        .tech-card ul {
          list-style: none;
          padding: 0;
          margin: 0;
          text-align: left;
        }

        .tech-card li {
          font-size: 0.8rem;
          margin-bottom: 6px;
          color: #e2e8f0;
        }

        /* User type sections */
        .user-type-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .user-type-header {
          border-radius: 12px;
          padding: 15px;
          color: white;
          text-align: center;
        }

        .user-type-blue { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); }
        .user-type-green { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .user-type-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
        .user-type-teal { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }

        .user-type-icon {
          width: 24px;
          height: 24px;
          margin-bottom: 8px;
        }

        .user-type-header h3 {
          font-size: 1.1rem;
          font-weight: 700;
          margin: 0 0 8px 0;
        }

        .user-type-header p {
          font-size: 0.8rem;
          margin: 0;
          opacity: 0.9;
        }

        .feature-group {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          padding: 12px;
          text-align: left;
        }

        .feature-group h4 {
          font-size: 0.9rem;
          font-weight: 600;
          margin: 0 0 8px 0;
          display: flex;
          align-items: center;
          color: #f1f5f9;
        }

        .feature-icon {
          width: 16px;
          height: 16px;
          margin-right: 6px;
        }

        .feature-group ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .feature-group li {
          font-size: 0.75rem;
          margin-bottom: 4px;
          color: #cbd5e1;
        }

        /* Data sources */
        .data-source-card {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 15px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .data-source-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .data-source-icon {
          width: 24px;
          height: 24px;
          margin-right: 10px;
          color: #3b82f6;
        }

        .data-source-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }

        .data-source-card ul {
          list-style: none;
          padding: 0;
          margin: 0 0 12px 0;
          text-align: left;
        }

        .data-source-card li {
          font-size: 0.8rem;
          margin-bottom: 6px;
          color: #e2e8f0;
        }

        .data-frequency {
          background: rgba(59, 130, 246, 0.2);
          color: #93c5fd;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 0.7rem;
          font-weight: 500;
          text-align: center;
        }

        .data-processing-card {
          background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
          border-radius: 12px;
          padding: 15px;
          color: white;
        }

        .data-processing-card h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 12px 0;
        }

        .processing-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .processing-item {
          display: flex;
          align-items: center;
          font-size: 0.8rem;
        }

        .processing-icon {
          width: 16px;
          height: 16px;
          margin-right: 8px;
        }

        /* Architecture sections */
        .arch-section {
          border-radius: 12px;
          padding: 18px;
          backdrop-filter: blur(10px);
        }

        .arch-section-blue { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; }
        .arch-section-green { background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; }
        .arch-section-white { background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.1); }
        .arch-section-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); color: white; }

        .arch-header {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
        }

        .arch-icon {
          width: 24px;
          height: 24px;
          margin-right: 10px;
        }

        .arch-header h3 {
          font-size: 1.2rem;
          font-weight: 700;
          margin: 0;
        }

        .arch-subsections {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
        }

        .arch-subsection h4 {
          font-size: 0.9rem;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .arch-subsection ul {
          list-style: none;
          padding: 0;
          margin: 0;
          text-align: left;
        }

        .arch-subsection li {
          font-size: 0.8rem;
          margin-bottom: 4px;
          opacity: 0.9;
        }

        .payment-items {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .payment-item {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 8px;
          padding: 10px;
        }

        .payment-item h4 {
          font-size: 0.9rem;
          font-weight: 600;
          margin: 0 0 4px 0;
        }

        .payment-item p {
          font-size: 0.8rem;
          margin: 0;
          opacity: 0.9;
        }

        /* Security sections */
        .security-section {
          border-radius: 12px;
          padding: 18px;
          backdrop-filter: blur(10px);
        }

        .security-section-red { 
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); 
          color: white; 
        }

        .security-header {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
        }

        .security-icon {
          width: 24px;
          height: 24px;
          margin-right: 10px;
        }

        .security-header h3 {
          font-size: 1.2rem;
          font-weight: 700;
          margin: 0;
        }

        .security-subsections {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .security-subsection h4 {
          font-size: 0.9rem;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .security-subsection ul {
          list-style: none;
          padding: 0;
          margin: 0;
          text-align: left;
        }

        .security-subsection li {
          font-size: 0.8rem;
          margin-bottom: 4px;
          opacity: 0.9;
        }

        .compliance-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .compliance-card {
          border-radius: 8px;
          padding: 12px;
          text-align: center;
        }

        .compliance-card-blue { background: rgba(59, 130, 246, 0.2); border: 1px solid #3b82f6; }
        .compliance-card-green { background: rgba(16, 185, 129, 0.2); border: 1px solid #10b981; }
        .compliance-card-purple { background: rgba(139, 92, 246, 0.2); border: 1px solid #8b5cf6; }
        .compliance-card-orange { background: rgba(245, 158, 11, 0.2); border: 1px solid #f59e0b; }

        .compliance-card h4 {
          font-size: 0.9rem;
          font-weight: 600;
          margin: 0 0 6px 0;
        }

        .compliance-card p {
          font-size: 0.7rem;
          margin: 0;
          opacity: 0.8;
        }

        .data-protection-section {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 15px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .data-protection-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .data-protection-icon {
          width: 20px;
          height: 20px;
          margin-right: 8px;
        }

        .data-protection-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }

        .protection-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .protection-item {
          border-left: 3px solid #3b82f6;
          padding-left: 10px;
        }

        .protection-item h4 {
          font-size: 0.8rem;
          font-weight: 600;
          margin: 0 0 2px 0;
        }

        .protection-item p {
          font-size: 0.7rem;
          margin: 0;
          opacity: 0.8;
        }

        .monitoring-section {
          background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
          border-radius: 12px;
          padding: 15px;
          color: white;
        }

        .monitoring-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .monitoring-icon {
          width: 20px;
          height: 20px;
          margin-right: 8px;
        }

        .monitoring-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }

        .monitoring-subsections {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .monitoring-subsection h4 {
          font-size: 0.8rem;
          font-weight: 600;
          margin: 0 0 6px 0;
        }

        .monitoring-subsection ul {
          list-style: none;
          padding: 0;
          margin: 0;
          text-align: left;
        }

        .monitoring-subsection li {
          font-size: 0.7rem;
          margin-bottom: 3px;
          opacity: 0.9;
        }

        /* Development sections */
        .dev-workflow-section {
          background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
          border-radius: 12px;
          padding: 18px;
          color: white;
        }

        .dev-header {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
        }

        .dev-icon {
          width: 24px;
          height: 24px;
          margin-right: 10px;
        }

        .dev-header h3 {
          font-size: 1.2rem;
          font-weight: 700;
          margin: 0;
        }

        .dev-subsections {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .dev-subsection h4 {
          font-size: 0.9rem;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .dev-subsection ul {
          list-style: none;
          padding: 0;
          margin: 0;
          text-align: left;
        }

        .dev-subsection li {
          font-size: 0.8rem;
          margin-bottom: 4px;
          opacity: 0.9;
        }

        .testing-strategies {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 15px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .testing-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .testing-icon {
          width: 20px;
          height: 20px;
          margin-right: 8px;
        }

        .testing-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }

        .testing-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .testing-item {
          background: rgba(16, 185, 129, 0.1);
          border-radius: 6px;
          padding: 8px;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .testing-item h4 {
          font-size: 0.8rem;
          font-weight: 600;
          margin: 0 0 2px 0;
          color: #6ee7b7;
        }

        .testing-item p {
          font-size: 0.7rem;
          margin: 0;
          opacity: 0.8;
        }

        .devops-section {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 15px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .devops-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .devops-icon {
          width: 20px;
          height: 20px;
          margin-right: 8px;
        }

        .devops-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }

        .devops-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .devops-item {
          border-left: 3px solid #3b82f6;
          padding-left: 10px;
        }

        .devops-item h4 {
          font-size: 0.8rem;
          font-weight: 600;
          margin: 0 0 2px 0;
        }

        .devops-item p {
          font-size: 0.7rem;
          margin: 0;
          opacity: 0.8;
        }

        .costs-section {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 12px;
          padding: 15px;
          color: white;
        }

        .costs-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .costs-icon {
          width: 20px;
          height: 20px;
          margin-right: 8px;
        }

        .costs-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }

        .costs-items {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .costs-item {
          display: flex;
          justify-content: space-between;
          font-size: 0.8rem;
        }

        .costs-total {
          display: flex;
          justify-content: space-between;
          font-size: 0.8rem;
          font-weight: 600;
          border-top: 1px solid rgba(255, 255, 255, 0.3);
          padding-top: 6px;
          margin-top: 6px;
        }

        .quality-metrics {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          border-radius: 12px;
          padding: 15px;
          color: white;
        }

        .quality-metrics h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 12px 0;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }

        .metric-item {
          text-align: center;
        }

        .metric-value {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .metric-label {
          font-size: 0.7rem;
          opacity: 0.9;
        }

        /* Layout styles */
        .two-column-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          width: 100%;
          margin: 15px 0;
        }

        .three-column-layout {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
          width: 100%;
          margin: 15px 0;
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .pitch-deck-container {
            height: 100vh;
            padding-top: 56px;
          }

          .slide {
            width: 98%;
            height: 92%;
            padding: 15px;
          }

          .nav-arrow {
            width: 45px;
            height: 45px;
            font-size: 1.3rem;
          }

          .nav-arrow-left {
            left: 10px;
          }

          .nav-arrow-right {
            right: 10px;
          }

          .two-column-layout,
          .three-column-layout {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .slide h1 {
            margin-bottom: 10px;
          }

          .slide h2 {
            margin-bottom: 15px;
          }

          .slide h3 {
            margin-bottom: 10px;
          }

          .slide h4 {
            margin-bottom: 8px;
          }

          .slide p, .slide li {
            margin-bottom: 8px;
          }

          .subtitle {
            margin-bottom: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default TechDeck;